import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeftRight,
  Banknote,
  CheckCircle2,
  Link2,
  Loader2,
  Plus,
  Unlink,
  XCircle,
} from 'lucide-react';
import {
  creerEncaissement,
  detacherEncaissement,
  ecarterEncaissement,
  fetchEncaissements,
  rapprocherEncaissement,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';

function euros(centimes: number): string {
  return `${(centimes / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

const ONGLETS = [
  { code: 'a_rapprocher', label: 'À ranger' },
  { code: 'rapproche', label: 'Rangés' },
  { code: 'ecarte', label: 'Écartés' },
];

/**
 * Les virements reçus, et ce à quoi ils correspondent.
 *
 * Le trésorier faisait ce travail entre deux onglets : son relevé bancaire
 * d'un côté, la liste des cotisations impayées de l'autre, et le doigt sur
 * l'écran. Ce va-et-vient disparaît — les virements dont le libellé porte une
 * référence arrivent déjà rangés, et les autres se rangent d'un clic.
 *
 * Rien n'est irréversible : un rapprochement se défait, et le versement posé
 * sur la cotisation part avec lui.
 */
export default function EncaissementsPage() {
  const { aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const peutEncaisser = aLeDroit('encaisser_paiements');

  const [onglet, setOnglet] = useState('a_rapprocher');
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [saisieOuverte, setSaisieOuverte] = useState(false);
  const [saisie, setSaisie] = useState({
    // Aujourd'hui par défaut : un virement se reporte le jour où on le voit.
    date: new Date().toISOString().slice(0, 10),
    montant: '',
    emetteur: '',
    libelle: '',
  });

  const query = useQuery({
    queryKey: ['encaissements', onglet],
    queryFn: () => fetchEncaissements(onglet),
  });

  const annoncer = (type: 'success' | 'error', message: string) => {
    setNotice({ type, message });
    if (type === 'success') setTimeout(() => setNotice(null), 4000);
  };

  const rafraichir = () => queryClient.invalidateQueries({ queryKey: ['encaissements'] });

  const enregistrer = useMutation({
    mutationFn: () =>
      creerEncaissement({
        date: saisie.date,
        montant: Number(String(saisie.montant).replace(',', '.')),
        emetteur: saisie.emetteur,
        libelle: saisie.libelle,
      }),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      // Le libellé et le montant changent d'une ligne à l'autre, la date
      // rarement : un trésorier reporte souvent plusieurs virements du même
      // jour, et la resaisir à chaque fois serait une corvée.
      setSaisie((prev) => ({ ...prev, montant: '', emetteur: '', libelle: '' }));
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  const ranger = useMutation({
    mutationFn: (id: number) => rapprocherEncaissement(id),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  const detacher = useMutation({
    mutationFn: (id: number) => detacherEncaissement(id),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  const ecarter = useMutation({
    mutationFn: ({ id, motif }: { id: number; motif: string }) => ecarterEncaissement(id, motif),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  if (query.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const encaissements: any[] = query.data?.encaissements ?? [];
  const comptes = query.data?.comptes ?? {};

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Banknote className="w-7 h-7 text-primary-700" />
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Encaissements</h1>
            <p className="text-sm text-gray-600">
              Les virements reçus sur le compte, et la pièce que chacun règle.
            </p>
          </div>
        </div>

        {peutEncaisser && (
          <button
            onClick={() => setSaisieOuverte((o) => !o)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Saisir un virement
          </button>
        )}
      </div>

      {notice && (
        <div
          className={`flex items-start gap-2 rounded-lg px-4 py-3 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {notice.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          <span>{notice.message}</span>
        </div>
      )}

      {/* La saisie d'un virement relevé sur le compte.
          Le libellé se recopie en entier : c'est en lui que se trouve la
          référence, et c'est elle qui retrouve la pièce toute seule. */}
      {saisieOuverte && peutEncaisser && (
        <div className="card lg:p-6">
          <h2 className="font-semibold text-gray-900">Reporter un virement</h2>
          <p className="mt-1 text-sm text-gray-600">
            Recopiez la ligne telle qu'elle figure sur votre relevé bancaire. Si le payeur y a
            porté sa référence, la cotisation, la facture ou le don se trouve tout seul.
          </p>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm text-gray-700">Date de l'opération</span>
              <input
                type="date"
                value={saisie.date}
                onChange={(e) => setSaisie({ ...saisie, date: e.target.value })}
                className="input-field mt-1"
              />
            </label>
            <label className="block">
              <span className="text-sm text-gray-700">Montant reçu (€)</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={saisie.montant}
                onChange={(e) => setSaisie({ ...saisie, montant: e.target.value })}
                className="input-field mt-1"
                placeholder="130,00"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-sm text-gray-700">
                Émetteur <span className="text-gray-400">(facultatif)</span>
              </span>
              <input
                value={saisie.emetteur}
                onChange={(e) => setSaisie({ ...saisie, emetteur: e.target.value })}
                className="input-field mt-1"
                placeholder="MARTIN SOPHIE"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-sm text-gray-700">Libellé, tel qu'il apparaît sur le relevé</span>
              <input
                value={saisie.libelle}
                onChange={(e) => setSaisie({ ...saisie, libelle: e.target.value })}
                className="input-field mt-1"
                placeholder="VIR SEPA 2VFZL2GULE97ZQRY COTISATION"
              />
            </label>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              onClick={() => enregistrer.mutate()}
              disabled={enregistrer.isPending || !saisie.date || !saisie.montant || !saisie.libelle}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
            >
              {enregistrer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Enregistrer
            </button>
            <button
              onClick={() => setSaisieOuverte(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-1">
        {ONGLETS.map((o) => (
          <button
            key={o.code}
            onClick={() => setOnglet(o.code)}
            className={`px-3.5 py-1.5 rounded-lg text-sm ${
              onglet === o.code ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {o.label}
            <span className="ml-1.5 opacity-70">{comptes[o.code] ?? 0}</span>
          </button>
        ))}
      </div>

      {encaissements.length === 0 ? (
        <div className="card lg:p-8 text-center text-sm text-gray-500">
          {onglet === 'a_rapprocher'
            ? 'Rien à ranger : tous les virements reçus sont rapprochés.'
            : 'Aucun encaissement dans cet état.'}
        </div>
      ) : (
        <div className="space-y-2">
          {encaissements.map((e) => (
            <article key={e.id} className="card lg:p-4">
              <div className="flex flex-wrap items-start gap-3">
                <span className="text-lg font-semibold tabular-nums text-gray-900 shrink-0">
                  {euros(e.montant)}
                </span>

                <span className="flex-1 min-w-[12rem]">
                  <span className="block text-sm text-gray-900">{e.emetteur ?? 'Émetteur inconnu'}</span>
                  <span className="block text-xs text-gray-500 break-words">
                    {e.dateOperation}
                    {e.libelle ? ` · ${e.libelle}` : ''}
                  </span>

                  {e.pieceLibelle && (
                    <span className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-green-100 text-green-800 px-2.5 py-0.5 text-xs">
                      <Link2 className="w-3 h-3" />
                      {e.pieceLibelle}
                      {e.automatique && <span className="opacity-70">· automatique</span>}
                    </span>
                  )}

                  {e.commentaire && (
                    <span className="block mt-1 text-xs text-gray-500 italic">{e.commentaire}</span>
                  )}
                </span>

                {peutEncaisser && (
                  <span className="flex items-center gap-2 shrink-0">
                    {e.etat === 'a_rapprocher' && (
                      <>
                        <button
                          onClick={() => ranger.mutate(e.id)}
                          disabled={ranger.isPending}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary-200 text-primary-700 text-sm font-medium hover:bg-primary-50"
                        >
                          <ArrowLeftRight className="w-4 h-4" />
                          Chercher la pièce
                        </button>
                        <button
                          onClick={() => {
                            const motif = window.prompt(
                              'Pourquoi écarter ce virement ? (remboursement, virement interne…)'
                            );
                            if (motif !== null) ecarter.mutate({ id: e.id, motif });
                          }}
                          title="Écarter"
                          className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}

                    {e.etat !== 'a_rapprocher' && (
                      <button
                        onClick={() => detacher.mutate(e.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
                      >
                        <Unlink className="w-4 h-4" />
                        Défaire
                      </button>
                    )}
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
